/**
 * MODULE: domain/pages/frontend/components/editor/DatabaseEmbedModal.tsx
 * PURPOSE: Pick a workspace database page and configure inline embed display
 */

import { useEffect, useMemo, useState } from 'react';
import { Button, Label, Modal, ModalBody, ModalFooter, ModalHeader, Select, TextInput } from 'flowbite-react';
import { HiOutlineDatabase, HiOutlineSearch } from 'react-icons/hi';
import type { PageTreeNode } from '../../../contracts/pages.types';
import { usePageTree } from '../../hooks/usePages';
import { flattenPageTree } from '../../utils/pageTreeUtils';
import {
  buildDatabaseEmbedContent,
  DATABASE_EMBED_ROW_LIMITS,
  DEFAULT_DATABASE_EMBED_MAX_ROWS,
  type DatabaseEmbedBlockContent,
} from '../../utils/pageDatabaseEmbedUtils';

type Props = {
  open: boolean;
  workspaceId: string;
  currentPageId: string;
  initial?: DatabaseEmbedBlockContent | null;
  onClose: () => void;
  onSubmit: (content: DatabaseEmbedBlockContent) => void;
};

export function DatabaseEmbedModal({
  open,
  workspaceId,
  currentPageId,
  initial,
  onClose,
  onSubmit,
}: Props) {
  const { data: tree = [], isLoading, isError } = usePageTree(workspaceId);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<PageTreeNode | null>(null);
  const [maxRows, setMaxRows] = useState(DEFAULT_DATABASE_EMBED_MAX_ROWS);
  const [searchFilter, setSearchFilter] = useState('');
  const [error, setError] = useState<string | null>(null);

  const databases = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return flattenPageTree(tree)
      .map((item) => item.node)
      .filter((node) => node.page_type === 'database' && node.id !== currentPageId)
      .filter((node) => {
        if (!normalized) return true;
        return (node.title || 'untitled').toLowerCase().includes(normalized);
      });
  }, [currentPageId, query, tree]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSearchFilter(initial?.search_filter ?? '');
    setMaxRows(initial?.max_rows ?? DEFAULT_DATABASE_EMBED_MAX_ROWS);
    setError(null);
    if (initial) {
      setSelected({
        id: initial.database_page_id,
        parent_page_id: null,
        title: initial.database_title,
        icon: initial.database_icon,
        page_type: 'database',
        position: 0,
        updated_at: '',
        children: [],
      });
    } else {
      setSelected(null);
    }
  }, [initial, open]);

  const handleSubmit = () => {
    if (!selected) {
      setError('Choose a database page to embed.');
      return;
    }
    const content = buildDatabaseEmbedContent({
      databasePageId: selected.id,
      workspaceId,
      databaseTitle: selected.title || 'Untitled database',
      databaseIcon: selected.icon,
      maxRows,
      searchFilter,
    });
    if (!content) {
      setError('Could not build embed configuration.');
      return;
    }
    onSubmit(content);
    onClose();
  };

  return (
    <Modal show={open} onClose={onClose} size="md">
      <ModalHeader>{initial ? 'Edit database embed' : 'Embed database'}</ModalHeader>
      <ModalBody data-testid="pages-database-embed-modal">
        {!initial ? (
          <>
            <TextInput
              icon={HiOutlineSearch}
              sizing="sm"
              placeholder="Search databases…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              aria-label="Search databases"
              data-testid="pages-database-embed-search"
            />
            <div className="mt-3 max-h-48 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
              {isLoading ? (
                <p className="px-3 py-4 text-sm text-gray-500">Loading databases…</p>
              ) : null}
              {isError ? (
                <p className="px-3 py-4 text-sm text-red-600">Could not load page tree.</p>
              ) : null}
              {!isLoading && !isError && databases.length === 0 ? (
                <p className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400">No database pages found.</p>
              ) : null}
              {!isLoading && !isError
                ? databases.map((database) => (
                    <button
                      key={database.id}
                      type="button"
                      className={`flex w-full items-center gap-2 border-b border-gray-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800 ${
                        selected?.id === database.id ? 'bg-blue-50 dark:bg-blue-950/30' : ''
                      }`}
                      onClick={() => setSelected(database)}
                      data-testid={`pages-database-embed-option-${database.id}`}
                    >
                      {database.icon ? (
                        <span className="text-base" aria-hidden>
                          {database.icon}
                        </span>
                      ) : (
                        <HiOutlineDatabase className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
                      )}
                      <span className="truncate font-medium">{database.title || 'Untitled database'}</span>
                    </button>
                  ))
                : null}
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-700 dark:text-gray-200">
            Embedded database:{' '}
            <span className="font-medium" data-testid="pages-database-embed-selected-title">
              {initial.database_title}
            </span>
          </p>
        )}

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="pages-database-embed-max-rows" className="mb-1 block text-sm">
              Max rows
            </Label>
            <Select
              id="pages-database-embed-max-rows"
              sizing="sm"
              value={String(maxRows)}
              onChange={(event) => setMaxRows(Number(event.target.value))}
              data-testid="pages-database-embed-max-rows"
            >
              {DATABASE_EMBED_ROW_LIMITS.map((limit) => (
                <option key={limit} value={limit}>
                  {limit} rows
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="pages-database-embed-filter" className="mb-1 block text-sm">
              Row filter
            </Label>
            <TextInput
              id="pages-database-embed-filter"
              sizing="sm"
              placeholder="Optional search…"
              value={searchFilter}
              onChange={(event) => setSearchFilter(event.target.value)}
              data-testid="pages-database-embed-filter"
            />
          </div>
        </div>
        {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
      </ModalBody>
      <ModalFooter>
        <Button color="gray" onClick={onClose}>
          Cancel
        </Button>
        <Button color="blue" onClick={handleSubmit} data-testid="pages-database-embed-submit">
          {initial ? 'Save embed' : 'Insert embed'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
